import type { CellClassParams, ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import type { Portfolio } from 'venn-api';
import {
  ALLOCATION_CELL_RENDERER,
  JUSTIFY_HEADER_END_CLASS,
  VALUE_CELL_RENDERER,
} from '../../studio-blocks/customAnalysisContants';
import { isNil, noop } from 'lodash';
import type { PortfolioAccessMode, RowData } from './types';

const useUploadPortfolioAllocatorColumnDefs = (): ColDef<RowData<Portfolio>>[] => {
  const columns: ColDef<RowData<Portfolio>>[] = [
    {
      headerName: 'Mapped?',
      cellRenderer: VALUE_CELL_RENDERER,
      colId: 'mapped',
      flex: 1,
    },
    {
      headerName: 'Allocation',
      cellRenderer: ALLOCATION_CELL_RENDERER,
      cellRendererParams: (params: ICellRendererParams) => {
        const { allocation, fund } = params.data.node;
        return {
          value: allocation,
          isRoot: params.node.level === 0,
          originalValue: allocation,
          isStrategy: isNil(fund),
          onUpdateAllocation: noop,
        };
      },
      colId: 'allocation',
      flex: 1,
      minWidth: 120,
    },
  ];

  return columns;
};

export const getRowData = (node: Portfolio, nodeId: number): RowData<Portfolio>[] => {
  if (node.fund) {
    return [
      {
        path: [nodeId],
        node,
      },
    ];
  }
  return [
    {
      path: [nodeId],
      node,
    },
    ...node.children.flatMap((child, childIndex) => {
      const childData = getRowData(child, childIndex);
      return childData.map((row) => {
        return {
          path: [nodeId, ...row.path],
          node: row.node,
        };
      });
    }),
  ];
};

const portfolioAllocatorStylingProps = {
  root: {
    fontWeight: 700,
    fontSize: 16,
  },
  strategy: {
    fontWeight: 700,
    fontSize: 14,
  },
  investment: {
    fontWeight: 400,
    fontSize: 14,
  },
};

const useAutoGroupColumnDefs = (accessMode: PortfolioAccessMode) => {
  return {
    flex: 5,
    minWidth: 200,
    wrapText: true,
    autoHeight: true,
    headerName: accessMode === 'OVERWRITE_EXISTING' ? 'Overwrite Entire Portfolio' : 'Create New Portfolio',
    field: 'path',
    headerClass: () => [JUSTIFY_HEADER_END_CLASS],
    valueGetter: (params: ValueGetterParams<RowData<Portfolio>>) => params.data?.node.name,
    cellStyle: (params: CellClassParams<RowData<Portfolio>>) => {
      const { level, data } = params.node;
      const baseStyle =
        level === 0
          ? portfolioAllocatorStylingProps.root
          : isNil(data?.node?.fund)
            ? portfolioAllocatorStylingProps.strategy
            : portfolioAllocatorStylingProps.investment;

      return {
        ...baseStyle,
        paddingLeft: 0,
      };
    },
    cellRendererParams: {
      suppressCount: true,
      suppressDoubleClickExpand: true,
    },
  };
};
export const useUploadPortfolioAllocator = (data: Portfolio, accessMode: PortfolioAccessMode) => {
  const columnDefs = useUploadPortfolioAllocatorColumnDefs();

  const rowData = getRowData(data, 0);

  const autoGroupColumnDefs = useAutoGroupColumnDefs(accessMode);

  return {
    rowData,
    columnDefs,
    autoGroupColumnDefs,
  };
};
